import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CommentOutlined,
  ReloadOutlined,
  SendOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import useFeedbacks from "hooks/feedbacks/useFeedbacks";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { formatDateTime } from "workspaces/workspaceUtils";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

const feelingOptions = [
  { label: "Like", value: "LIKE" },
  { label: "Happy", value: "HAPPY" },
  { label: "Dislike", value: "DISLIKE" },
  { label: "Sad", value: "SAD" },
];

const subjectOptions = [
  { label: "General Feedback", value: "GENERAL_FEEDBACK" },
  { label: "Feature Request", value: "FEATURE_REQUEST" },
  { label: "App Bug", value: "APP_BUG" },
  { label: "UI Feedback", value: "UI_FEEDBACK" },
  { label: "Payments", value: "PAYMENTS" },
  { label: "Tasks", value: "TASKS" },
  { label: "Support", value: "SUPPORT" },
  { label: "Performance", value: "PERFORMANCE" },
  { label: "Account", value: "ACCOUNT" },
  { label: "Verification", value: "VERIFICATION" },
  { label: "Safety", value: "SAFETY" },
  { label: "Harassment", value: "HARASSMENT" },
  { label: "Other", value: "OTHER" },
];

const statusColor = (status = "") => {
  const normalized = String(status).toUpperCase();
  if (normalized.includes("RESOLVED") || normalized.includes("DONE")) return "green";
  if (normalized.includes("ARCHIVED") || normalized.includes("CLOSED")) return "default";
  if (normalized.includes("REVIEW")) return "blue";
  return "gold";
};

const feelingColor = (feeling = "") => {
  const normalized = String(feeling).toUpperCase();
  if (normalized === "LIKE" || normalized === "HAPPY") return "green";
  if (normalized === "DISLIKE" || normalized === "SAD") return "red";
  return "blue";
};

const Feedback = () => {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const {
    feedbacks,
    loading,
    totalElements,
    fetchAllFeedbacks,
    createFeedback,
  } = useFeedbacks();

  const userId = user?.id || user?.userId || "";

  const columns = useMemo(
    () => [
      {
        title: "Subject",
        dataIndex: "subject",
        key: "subject",
        width: 190,
        render: (value) => (
          <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
            {value
              ? value
                  .split("_")
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                  .join(" ")
              : "General Feedback"}
          </Text>
        ),
      },
      {
        title: "Feeling",
        dataIndex: "feeling",
        key: "feeling",
        width: 130,
        render: (value) => <Tag color={feelingColor(value)}>{value || "-"}</Tag>,
      },
      {
        title: "Feedback",
        dataIndex: "content",
        key: "content",
        render: (value) => (
          <Text style={{ color: OLIVEEB_COLORS.textMuted }}>
            {value || "-"}
          </Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 130,
        render: (value) => <Tag color={statusColor(value)}>{value || "PENDING"}</Tag>,
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 210,
        render: (value) => formatDateTime(value) || "-",
      },
    ],
    [],
  );

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const created = await createFeedback({
        userId: userId || undefined,
        subject: values.subject,
        feeling: values.feeling,
        content: values.content,
      });
      if (created) {
        form.resetFields();
        toast.success("Feedback submitted successfully");
      } else {
        toast.error("Could not submit feedback. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<CommentOutlined />}
          title="Feedback"
          subtitle="Share ideas, issues, and product notes with the Olreadia team"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => fetchAllFeedbacks()}
          loading={loading && !submitting}
          style={{
            borderColor: OLIVEEB_COLORS.oliveebGreen,
            color: OLIVEEB_COLORS.oliveebGreen,
            fontWeight: 700,
          }}
        >
          Refresh
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card
            style={{
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
              boxShadow: "0 14px 34px rgba(6, 105, 2, 0.08)",
            }}
          >
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
              <span
                style={{
                  display: "inline-grid",
                  placeItems: "center",
                  width: 46,
                  height: 46,
                  borderRadius: 12,
                  background: OLIVEEB_COLORS.oliveebGreenSoft,
                  color: OLIVEEB_COLORS.oliveebGreen,
                  fontSize: 20,
                }}
              >
                <SmileOutlined />
              </span>
              <div>
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen, fontSize: 18 }}>
                  Tell us what would make Olreadia better
                </Text>
                <Paragraph style={{ margin: "6px 0 0", color: OLIVEEB_COLORS.textMuted }}>
                  Send product ideas, workflow friction, quality notes, or anything your team needs.
                </Paragraph>
              </div>

              <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                initialValues={{ subject: "GENERAL_FEEDBACK", feeling: undefined }}
                onFinish={handleSubmit}
              >
                <Form.Item
                  label="Subject"
                  name="subject"
                  rules={[{ required: true, message: "Select a subject" }]}
                >
                  <Select options={subjectOptions} />
                </Form.Item>
                <Form.Item
                  label="Feeling"
                  name="feeling"
                  rules={[{ required: true, message: "Select how this feels" }]}
                >
                  <Select options={feelingOptions} />
                </Form.Item>
                <Form.Item
                  label="Feedback"
                  name="content"
                  rules={[
                    { required: true, message: "Enter your feedback" },
                    { min: 8, message: "Add a little more detail" },
                  ]}
                >
                  <TextArea
                    rows={6}
                    placeholder="What should we improve, fix, or consider?"
                    showCount
                    maxLength={1200}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SendOutlined />}
                  loading={submitting}
                  block
                  style={{
                    height: 44,
                    borderRadius: 999,
                    fontWeight: 900,
                    background: OLIVEEB_COLORS.oliveebGreen,
                    borderColor: OLIVEEB_COLORS.oliveebGreen,
                  }}
                >
                  Submit Feedback
                </Button>
              </Form>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card
            title={
              <Space>
                <CommentOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue }} />
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Recent Feedback
                </Text>
                <Tag color="blue">{totalElements}</Tag>
              </Space>
            }
            style={{
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
            }}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 14, borderRadius: 8 }}
              message="Feedback helps prioritize the small details that make enterprise project work smoother."
            />
            <Table
              columns={columns}
              dataSource={feedbacks}
              rowKey={(record) => record.id || `${record.subject}-${record.createdAt}`}
              loading={loading}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              scroll={{ x: 760 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Feedback;
